import React from "react";
import { gql, useQuery } from "@apollo/client";
import { withStyles } from "@material-ui/core/styles";
import TablePagination from "@material-ui/core/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
  Typography
} from "@material-ui/core";
import TableLink from "../TableLink";

const styles = theme => ({
  root: {
    marginTop: theme.spacing(3),
    overflowX: "auto",
    margin: "auto"
  }
});

const GET_HISTORY_GAMES = gql`
  query playerGameQuery(
    $first: Int
    $offset: Int
    $filter: _GameFilter
    $playerPerformancesFilter: _PlayerPerformanceFilter
  ) {
    Game(first: $first, offset: $offset, filter: $filter) {
      id
      scoreline
      round {
        tournament {
          id
          name
        }
      }
      player_performances(filter: $playerPerformancesFilter) {
        player {
          id
        }
        powers
        gets
        negs
        points
      }
      team_performances {
        team {
          school {
            id
          }
        }
        powers
        gets
        negs
        points
      }
    }
  }
`;

function HistoryGameList(props) {
  const {
    classes,
    id,
    opponentId,
    type,
    opponentType,
    totalGames,
    mainName,
    opponentName
  } = props;
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("tournament_date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const getFilter = () => {
    var mainFilter =
      type === "Player"
        ? { player_performances_some: { player: { id } } }
        : { team_performances_some: { team: { school: { id } } } };
    var opponentFilter =
      opponentType === "Player"
        ? { player_performances_some: { player: { id: opponentId } } }
        : { team_performances_some: { team: { school: { id: opponentId } } } };

    return {
      AND: [mainFilter, opponentFilter]
    };
  };

  const getPlayerPerformanceFilter = () => {
    let ids_in = [];

    if (type === "Player") ids_in.push(id);
    if (opponentType === "Players") ids_in.push(opponentId);

    return { player: { ids_in } };
  };

  const { loading, data, error } = useQuery(GET_HISTORY_GAMES, {
    variables: {
      first: rowsPerPage,
      offset: rowsPerPage * page,
      filter: getFilter(),
      playerPerformanceFilter: getPlayerPerformanceFilter()
      //orderBy: orderBy.includes("tournament") ? [`${orderBy}_${order}`,`round_number_${order}`] : `${orderBy}_${order}`
    }
  });

  const handleChangePage = (_, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (_, rows) => {
    setRowsPerPage(parseInt(rows.key));
  };

  const handleSortRequest = property => {
    const newOrderBy = property;
    let newOrder = "desc";

    if (orderBy === property && order === "desc") {
      newOrder = "asc";
    }

    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6">Game History</Typography>
      {loading && !error && <p>Loading...</p>}
      {error && !loading && <p>Error</p>}
      {data && !loading && !error && (
        <div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell
                  style={{ overflow: "visible", whiteSpace: "nowrap" }}
                >
                  {mainName}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell style={{ overflow: "visible" }}>
                  {opponentName}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  key="tournament_name"
                  sortDirection={orderBy === "tournament_name" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "tournament_name"}
                      direction={order}
                      onClick={() => handleSortRequest("tournament_name")}
                    >
                      Tournament
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="scoreline"
                  sortDirection={orderBy === "scoreline" ? order : false}
                >
                  <Tooltip title="Sort" placement="bottom-end" enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === "scoreline"}
                      direction={order}
                      onClick={() => handleSortRequest("scoreline")}
                    >
                      Scoreline
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell key="main_powers">
                  <Tooltip title="Sort" placement="bottom-end" enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === "main_powers"}
                      direction={order}
                      onClick={() => handleSortRequest("main_powers")}
                    >
                      Powers
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="main_gets"
                  sortDirection={orderBy === "main_gets" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "main_gets"}
                      direction={order}
                      onClick={() => handleSortRequest("main_gets")}
                    >
                      Gets
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="main_negs"
                  sortDirection={orderBy === "main_negs" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "main_negs"}
                      direction={order}
                      onClick={() => handleSortRequest("main_negs")}
                    >
                      Negs
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="main_points"
                  sortDirection={orderBy === "main_points" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "main_points"}
                      direction={order}
                      onClick={() => handleSortRequest("main_points")}
                    >
                      Points
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell key="opponent_powers">
                  <Tooltip title="Sort" placement="bottom-end" enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === "opponent_powers"}
                      direction={order}
                      onClick={() => handleSortRequest("opponent_powers")}
                    >
                      Powers
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="opponent_gets"
                  sortDirection={orderBy === "opponent_gets" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "opponent_gets"}
                      direction={order}
                      onClick={() => handleSortRequest("opponent_gets")}
                    >
                      Gets
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="opponent_negs"
                  sortDirection={orderBy === "opponent_negs" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "opponent_negs"}
                      direction={order}
                      onClick={() => handleSortRequest("opponent_negs")}
                    >
                      Negs
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="opponent_points"
                  sortDirection={orderBy === "opponent_points" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "opponent_points"}
                      direction={order}
                      onClick={() => handleSortRequest("opponent_points")}
                    >
                      Points
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.Game.map(game => {
                let g = { ...game };
                g.main =
                  type === "School"
                    ? g.team_performances.find(tp => tp.team?.school?.id === id)
                    : g.player_performances.find(tp => tp.player?.id === id);
                g.opponent =
                  opponentType === "School"
                    ? g.team_performances.find(
                        tp => tp.team?.school?.id === opponentId
                      )
                    : g.player_performances.find(
                        tp => tp.player?.id === opponentId
                      );

                return (
                  <TableRow key={g.id}>
                    <TableCell component="th" scope="row">
                      <TableLink to={`/tournament/${g.round.tournament.id}`}>
                        {g.round.tournament.name}
                      </TableLink>
                    </TableCell>
                    <TableCell>
                      <TableLink
                        to={`/tournament/${g.round.tournament.id}/game-detail/${g.id}`}
                      >
                        {g.scoreline}
                      </TableLink>
                    </TableCell>
                    <TableCell>{g.main.powers}</TableCell>
                    <TableCell>{g.main.gets}</TableCell>
                    <TableCell>{g.main.negs}</TableCell>
                    <TableCell>{g.main.points}</TableCell>
                    <TableCell>{g.opponent.powers}</TableCell>
                    <TableCell>{g.opponent.gets}</TableCell>
                    <TableCell>{g.opponent.negs}</TableCell>
                    <TableCell>{g.opponent.points}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalGames}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      )}
    </div>
  );
}

export default withStyles(styles)(HistoryGameList);
